import React from 'react';
import { IBusinessProfile } from '../../../../../../../Interfaces/responseTypes';
import useLanguagesHooks from '../../../../../../../Components/Languages/setLanguage-hooks';
import { CalendarIcon, ClockIcon } from '../../../../../../../Components/IconComponents';
import { useBusinessClosedNotice } from './businessClosedNotice-hook';
import styles from './businessClosedNotice.module.scss';

type BusinessClosedNoticeVariant = 'page' | 'cart' | 'order';

interface IProps {
  profile: IBusinessProfile;
  variant?: BusinessClosedNoticeVariant;
}

const BusinessClosedNotice: React.FC<IProps> = ({ profile, variant = 'page' }) => {
  const { t } = useLanguagesHooks();
  const { expanded, toggle, rootRef, freeDays, dailyHours } = useBusinessClosedNotice(profile);
  const isCart = variant === 'cart';
  const isOrder = variant === 'order';

  return (
    <div
      ref={rootRef}
      className={`${styles.wrapper} ${isCart ? styles.cartWrapper : ''} ${isOrder ? styles.orderWrapper : ''} ${!isCart && !isOrder ? styles.pageWrapper : ''}`}
    >
      <button
        type="button"
        className={`${styles.statusButton} ${expanded ? styles.statusButtonExpanded : ''}`}
        onClick={toggle}
        aria-expanded={expanded}
        aria-label={t('Visitor.Business_Closed.Tap_For_Hours')}
      >
        <span className={styles.statusDot} aria-hidden="true" />
        <span className={styles.statusText}>
          <span className={styles.statusLabel}>
            {t('My_Business.Business_Profile.Page.Close')}
          </span>
        </span>
        <span
          className={`${styles.chevron} ${expanded ? styles.chevronUp : ''}`}
          aria-hidden="true"
        />
      </button>

      <div
        className={`${styles.hoursPanel} ${isCart ? styles.hoursPanelCart : ''} ${isOrder ? styles.hoursPanelOrder : ''} ${!isCart && !isOrder ? styles.hoursPanelPage : ''} ${expanded ? styles.hoursPanelOpen : ''}`}
        role="region"
        aria-hidden={!expanded}
      >
        <div className={styles.hoursPanelInner}>
          <div className={styles.hoursPanelContent}>
            <p className={styles.panelTitle}>{t('Visitor.Business_Closed.Working_Hours')}</p>

            <div className={styles.scheduleRow}>
              <span className={styles.scheduleIcon}>
                <ClockIcon width={18} height={18} color="var(--primary-hover)" />
              </span>
              <p className={styles.scheduleValue}>
                {profile.is_24
                  ? t('Visitor.Business_Closed.Hours_24')
                  : dailyHours ?? '—'}
              </p>
            </div>

            <div className={styles.scheduleRow}>
              <span className={styles.scheduleIcon}>
                <CalendarIcon width={18} height={18} color="var(--primary-hover)" />
              </span>
              <p className={styles.scheduleValue}>
                {profile.full_week
                  ? t('My_Business.Business_Profile.Page.Full_Week')
                  : freeDays.length > 0
                    ? (
                      <>
                        {t('Visitor.Business_Closed.Days_Off')}
                        {' '}
                        {freeDays.join(', ')}
                      </>
                    )
                    : t('Visitor.Business_Closed.Every_Day')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessClosedNotice;
